import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { vipsService, VIP, VIPCreate } from '../services/vips';
import { suppliersService, Supplier, SupplierCreate, KeyPerson } from '../services/suppliers';
import { Button } from '../components/UI/Button';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const Registration: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [vips, setVips] = useState<VIP[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [newVip, setNewVip] = useState<VIPCreate>({ full_name: '', cpf: '', residential_address: '', email: '', phone: '', role_title: '', company_name: '', notes: '', is_active: true });
  const [newSupplier, setNewSupplier] = useState<SupplierCreate>({ name: '', domain: '', contact_email: '', contact_phone: '', cnpj: '', notes: '', key_people: [], is_active: true });

  // Editing state for VIPs
  const [editingVipId, setEditingVipId] = useState<number | null>(null);
  const [editVip, setEditVip] = useState<Partial<VIP>>({});

  // Editing state for Suppliers
  const [editingSupplierId, setEditingSupplierId] = useState<number | null>(null);
  const [editSupplier, setEditSupplier] = useState<Partial<Supplier>>({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [vipsData, suppliersData] = await Promise.all([
        vipsService.getAll(),
        suppliersService.getActive(),
      ]);
      setVips(vipsData);
      setSuppliers(suppliersData);
    } catch (e) {
      toast.error(t('registration.toasts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateVip = async () => {
    try {
      setLoading(true);
      const created = await vipsService.create(newVip);
      setVips([created, ...vips]);
      setNewVip({ full_name: '', cpf: '', residential_address: '', email: '', phone: '', role_title: '', company_name: '', notes: '', is_active: true });
      toast.success(t('registration.toasts.createVipSuccess'));
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const startEditVip = (vip: VIP) => {
    setEditingVipId(vip.id);
    setEditVip({
      full_name: vip.full_name,
      cpf: vip.cpf,
      residential_address: vip.residential_address,
      email: vip.email,
      phone: vip.phone,
      role_title: vip.role_title,
      company_name: vip.company_name,
      notes: vip.notes,
      is_active: vip.is_active,
    });
  };

  const cancelEditVip = () => {
    setEditingVipId(null);
    setEditVip({});
  };

  const saveEditVip = async () => {
    if (editingVipId == null) return;
    try {
      setLoading(true);
      const updated = await vipsService.update(editingVipId, {
        full_name: editVip.full_name,
        cpf: editVip.cpf,
        residential_address: editVip.residential_address,
        email: editVip.email,
        phone: editVip.phone,
        role_title: editVip.role_title,
        company_name: editVip.company_name,
        notes: editVip.notes,
        is_active: editVip.is_active,
      });
      setVips(vips.map(v => (v.id === updated.id ? updated : v)));
      toast.success(t('registration.toasts.updateVipSuccess'));
      cancelEditVip();
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVip = async (id: number, name: string) => {
    const confirmed = window.confirm(t('registration.actions.confirmDelete', { name }));
    if (!confirmed) return;
    try {
      setLoading(true);
      await vipsService.delete(id);
      setVips(vips.filter(v => v.id !== id));
      toast.success(t('registration.toasts.deleteVipSuccess'));
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = async () => {
    try {
      setLoading(true);
      const created = await suppliersService.create(newSupplier);
      setSuppliers([created, ...suppliers]);
      setNewSupplier({ name: '', domain: '', contact_email: '', contact_phone: '', cnpj: '', notes: '', is_active: true });
      toast.success(t('registration.toasts.createSupplierSuccess'));
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const startEditSupplier = (supplier: Supplier) => {
    setEditingSupplierId(supplier.id);
    setEditSupplier({
      name: supplier.name,
      domain: supplier.domain,
      cnpj: supplier.cnpj,
      contact_email: supplier.contact_email,
      contact_phone: supplier.contact_phone,
      notes: supplier.notes,
      is_active: supplier.is_active,
    });
  };

  const cancelEditSupplier = () => {
    setEditingSupplierId(null);
    setEditSupplier({});
  };

  const saveEditSupplier = async () => {
    if (editingSupplierId == null) return;
    try {
      setLoading(true);
      const updated = await suppliersService.update(editingSupplierId, {
        name: editSupplier.name,
        domain: editSupplier.domain,
        cnpj: editSupplier.cnpj,
        contact_email: editSupplier.contact_email,
        contact_phone: editSupplier.contact_phone,
        notes: editSupplier.notes,
        is_active: editSupplier.is_active,
      });
      setSuppliers(suppliers.map(s => (s.id === updated.id ? updated : s)));
      toast.success(t('registration.toasts.updateSupplierSuccess'));
      cancelEditSupplier();
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (id: number, name: string) => {
    const confirmed = window.confirm(t('registration.actions.confirmDeleteSupplier', { name }));
    if (!confirmed) return;
    try {
      setLoading(true);
      await suppliersService.delete(id);
      setSuppliers(suppliers.filter(s => s.id !== id));
      toast.success(t('registration.toasts.deleteSupplierSuccess'));
    } catch (e) {
      toast.error(t('registration.toasts.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6">VIPs e Fornecedores</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* VIPs */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('registration.vips')}</h2>

          {/* Form - VIP */}
          <div className="space-y-3 mb-4">
            <div>
              <label className="label">{t('registration.form.fullName')}</label>
              <input className="input" value={newVip.full_name || ''} onChange={(e) => setNewVip({ ...newVip, full_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('registration.form.cpf')}</label>
                <input className="input" value={newVip.cpf || ''} onChange={(e) => setNewVip({ ...newVip, cpf: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('registration.form.residentialAddress')}</label>
                <input className="input" value={newVip.residential_address || ''} onChange={(e) => setNewVip({ ...newVip, residential_address: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('registration.form.email')}</label>
                <input className="input" value={newVip.email || ''} onChange={(e) => setNewVip({ ...newVip, email: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('registration.form.phone')}</label>
                <input className="input" value={newVip.phone || ''} onChange={(e) => setNewVip({ ...newVip, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('registration.form.roleTitle')}</label>
                <input className="input" value={newVip.role_title || ''} onChange={(e) => setNewVip({ ...newVip, role_title: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('registration.form.companyName')}</label>
                <input className="input" value={newVip.company_name || ''} onChange={(e) => setNewVip({ ...newVip, company_name: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label">{t('registration.form.notes')}</label>
              <textarea className="input" rows={3} value={newVip.notes || ''} onChange={(e) => setNewVip({ ...newVip, notes: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={newVip.is_active ?? true} onChange={(e) => setNewVip({ ...newVip, is_active: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">{t('registration.form.isActive')}</span>
            </div>
            <button className="btn btn-primary" onClick={handleCreateVip} disabled={loading || !newVip.full_name}>
              {t('registration.actions.create')}
            </button>
          </div>

          {/* List - VIPs */}
          {loading && <p className="text-sm text-gray-600 dark:text-gray-400">{t('registration.loading')}</p>}
          {!loading && vips.length === 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('registration.emptyVips')}</p>
          )}
          {!loading && vips.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.fullName')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.cpf')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.residentialAddress')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.email')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.phone')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {vips.map((vip) => (
                    <tr key={vip.id}>
                    {editingVipId === vip.id ? (
                      <>
                        <td className="px-3 py-2">
                          <input className="input" value={editVip.full_name || ''} onChange={(e) => setEditVip({ ...editVip, full_name: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editVip.cpf || ''} onChange={(e) => setEditVip({ ...editVip, cpf: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editVip.residential_address || ''} onChange={(e) => setEditVip({ ...editVip, residential_address: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editVip.email || ''} onChange={(e) => setEditVip({ ...editVip, email: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editVip.phone || ''} onChange={(e) => setEditVip({ ...editVip, phone: e.target.value })} />
                        </td>
                        <td className="px-3 py-2 flex gap-2 whitespace-nowrap">
                          <button className="btn btn-primary" onClick={saveEditVip} disabled={loading || !editVip.full_name}>{t('registration.actions.save')}</button>
                          <button className="btn" onClick={cancelEditVip}>{t('registration.actions.cancel')}</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{vip.full_name}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{vip.cpf || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{vip.residential_address || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{vip.email || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{vip.phone || '-'}</td>
                        <td className="px-3 py-2 flex gap-2 whitespace-nowrap">
                          <Button
                            variant="default"
                            size="icon"
                            onClick={() => startEditVip(vip)}
                            aria-label={t('registration.actions.edit')}
                            className="p-0"
                            style={{ height: '2rem', width: '2rem' }}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => handleDeleteVip(vip.id, vip.full_name)}
                            aria-label={t('registration.actions.delete')}
                            className="p-0"
                            style={{ height: '2rem', width: '2rem' }}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Suppliers */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('registration.suppliers')}</h2>

          {/* Form - Supplier */}
          <div className="space-y-3 mb-4">
            <div>
              <label className="label">{t('registration.form.name')}</label>
              <input className="input" value={newSupplier.name || ''} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('registration.form.domain')}</label>
              <input className="input" value={newSupplier.domain || ''} onChange={(e) => setNewSupplier({ ...newSupplier, domain: e.target.value })} />
            </div>
            <div>
              <label className="label">{t('registration.form.cnpj')}</label>
              <input className="input" value={newSupplier.cnpj || ''} onChange={(e) => setNewSupplier({ ...newSupplier, cnpj: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label">{t('registration.form.email')}</label>
                <input className="input" value={newSupplier.contact_email || ''} onChange={(e) => setNewSupplier({ ...newSupplier, contact_email: e.target.value })} />
              </div>
              <div>
                <label className="label">{t('registration.form.phone')}</label>
                <input className="input" value={newSupplier.contact_phone || ''} onChange={(e) => setNewSupplier({ ...newSupplier, contact_phone: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="label">{t('registration.form.notes')}</label>
              <textarea className="input" rows={3} value={newSupplier.notes || ''} onChange={(e) => setNewSupplier({ ...newSupplier, notes: e.target.value })} />
            </div>
            {/* Key People - Supplier */}
            <div>
              <label className="label">{t('registration.form.keyPeople')}</label>
              <div className="space-y-2">
                {(newSupplier.key_people || []).map((kp, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input
                      className="input"
                      placeholder={t('registration.form.name') as string}
                      value={kp.name || ''}
                      onChange={(e) => {
                        const arr = [...(newSupplier.key_people || [])];
                        arr[idx] = { ...arr[idx], name: e.target.value } as KeyPerson;
                        setNewSupplier({ ...newSupplier, key_people: arr });
                      }}
                    />
                    <input
                      className="input"
                      placeholder={t('registration.form.roleTitle') as string}
                      value={kp.role_title || ''}
                      onChange={(e) => {
                        const arr = [...(newSupplier.key_people || [])];
                        arr[idx] = { ...arr[idx], role_title: e.target.value } as KeyPerson;
                        setNewSupplier({ ...newSupplier, key_people: arr });
                      }}
                    />
                    <input
                      className="input"
                      placeholder={t('registration.form.email') as string}
                      value={kp.email || ''}
                      onChange={(e) => {
                        const arr = [...(newSupplier.key_people || [])];
                        arr[idx] = { ...arr[idx], email: e.target.value } as KeyPerson;
                        setNewSupplier({ ...newSupplier, key_people: arr });
                      }}
                    />
                    <div className="flex gap-2">
                      <input
                        className="input flex-1"
                        placeholder={t('registration.form.phone') as string}
                        value={kp.phone || ''}
                        onChange={(e) => {
                          const arr = [...(newSupplier.key_people || [])];
                          arr[idx] = { ...arr[idx], phone: e.target.value } as KeyPerson;
                          setNewSupplier({ ...newSupplier, key_people: arr });
                        }}
                      />
                      <Button
                        variant="destructive"
                        onClick={() => {
                          const arr = [...(newSupplier.key_people || [])];
                          arr.splice(idx, 1);
                          setNewSupplier({ ...newSupplier, key_people: arr });
                        }}
                      >
                        {t('registration.actions.delete')}
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => setNewSupplier({ ...newSupplier, key_people: [ ...(newSupplier.key_people || []), { name: '', role_title: '', email: '', phone: '' } ] })}
                >
                  {t('registration.actions.addKeyPerson')}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={newSupplier.is_active ?? true} onChange={(e) => setNewSupplier({ ...newSupplier, is_active: e.target.checked })} />
              <span className="text-sm text-gray-700 dark:text-gray-300">{t('registration.form.isActive')}</span>
            </div>
            <button className="btn btn-primary" onClick={handleCreateSupplier} disabled={loading || !newSupplier.name}>
              {t('registration.actions.create')}
            </button>
          </div>

          {/* List - Suppliers */}
          {loading && <p className="text-sm text-gray-600 dark:text-gray-400">{t('registration.loading')}</p>}
          {!loading && suppliers.length === 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('registration.emptySuppliers')}</p>
          )}
          {!loading && suppliers.length > 0 && (
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.name')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.domain')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.cnpj')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.email')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.phone')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('registration.form.keyPeople')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {suppliers.map((s) => (
                  <tr key={s.id}>
                    {editingSupplierId === s.id ? (
                      <>
                        <td className="px-3 py-2">
                          <input className="input" value={editSupplier.name || ''} onChange={(e) => setEditSupplier({ ...editSupplier, name: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editSupplier.domain || ''} onChange={(e) => setEditSupplier({ ...editSupplier, domain: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editSupplier.cnpj || ''} onChange={(e) => setEditSupplier({ ...editSupplier, cnpj: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editSupplier.contact_email || ''} onChange={(e) => setEditSupplier({ ...editSupplier, contact_email: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input" value={editSupplier.contact_phone || ''} onChange={(e) => setEditSupplier({ ...editSupplier, contact_phone: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <div className="space-y-2">
                            {((editSupplier.key_people as KeyPerson[]) || []).map((kp, idx) => (
                              <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <input className="input" placeholder={t('registration.form.name') as string} value={kp.name || ''} onChange={(e) => {
                                  const arr = [ ...(((editSupplier.key_people as KeyPerson[]) || [])) ];
                                  arr[idx] = { ...arr[idx], name: e.target.value } as KeyPerson;
                                  setEditSupplier({ ...editSupplier, key_people: arr });
                                }} />
                                <input className="input" placeholder={t('registration.form.roleTitle') as string} value={kp.role_title || ''} onChange={(e) => {
                                  const arr = [ ...(((editSupplier.key_people as KeyPerson[]) || [])) ];
                                  arr[idx] = { ...arr[idx], role_title: e.target.value } as KeyPerson;
                                  setEditSupplier({ ...editSupplier, key_people: arr });
                                }} />
                                <input className="input" placeholder={t('registration.form.email') as string} value={kp.email || ''} onChange={(e) => {
                                  const arr = [ ...(((editSupplier.key_people as KeyPerson[]) || [])) ];
                                  arr[idx] = { ...arr[idx], email: e.target.value } as KeyPerson;
                                  setEditSupplier({ ...editSupplier, key_people: arr });
                                }} />
                                <div className="flex gap-2">
                                  <input className="input flex-1" placeholder={t('registration.form.phone') as string} value={kp.phone || ''} onChange={(e) => {
                                    const arr = [ ...(((editSupplier.key_people as KeyPerson[]) || [])) ];
                                    arr[idx] = { ...arr[idx], phone: e.target.value } as KeyPerson;
                                    setEditSupplier({ ...editSupplier, key_people: arr });
                                  }} />
                                  <Button variant="destructive" onClick={() => {
                                    const arr = [ ...(((editSupplier.key_people as KeyPerson[]) || [])) ];
                                    arr.splice(idx, 1);
                                    setEditSupplier({ ...editSupplier, key_people: arr });
                                  }}>{t('registration.actions.delete')}</Button>
                                </div>
                              </div>
                            ))}
                            <Button onClick={() => setEditSupplier({ ...editSupplier, key_people: [ ...(((editSupplier.key_people as KeyPerson[]) || [])), { name: '', role_title: '', email: '', phone: '' } ] })}>
                              {t('registration.actions.addKeyPerson')}
                            </Button>
                          </div>
                        </td>
                        <td className="px-3 py-2 flex gap-2 whitespace-nowrap">
                          <button className="btn btn-primary" onClick={saveEditSupplier} disabled={loading || !editSupplier.name}>{t('registration.actions.save')}</button>
                          <button className="btn" onClick={cancelEditSupplier}>{t('registration.actions.cancel')}</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{s.name}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{s.domain || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{s.cnpj || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{s.contact_email || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{s.contact_phone || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                          {(s.key_people || []).length > 0 ? (
                            <ul className="list-disc ml-4">
                              {(s.key_people || []).map((kp, idx) => (
                                <li key={idx}>{kp.name}{kp.role_title ? ` (${kp.role_title})` : ''}</li>
                              ))}
                            </ul>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 flex gap-2 whitespace-nowrap">
                          <Button
                            variant="default"
                            size="icon"
                            onClick={() => startEditSupplier(s)}
                            aria-label={t('registration.actions.edit')}
                            className="p-0"
                            style={{ height: '2rem', width: '2rem' }}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => handleDeleteSupplier(s.id, s.name)}
                            aria-label={t('registration.actions.delete')}
                            className="p-0"
                            style={{ height: '2rem', width: '2rem' }}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Registration;